import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { RESTRICTIONS, BRAZIL_STATES } from "../constants/restrictions";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", address: "", city: "", state: "SP", description: "",
  restrictions: [], website: "", phone: "", latitude: "", longitude: "", status: "pending",
};

export default function EditPlaceModal({ open, place, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (place) {
      setForm({
        name: place.name || "",
        address: place.address || "",
        city: place.city || "",
        state: place.state || "SP",
        description: place.description || "",
        restrictions: place.restrictions || [],
        website: place.website || "",
        phone: place.phone || "",
        latitude: place.latitude != null ? String(place.latitude) : "",
        longitude: place.longitude != null ? String(place.longitude) : "",
        status: place.status || "pending",
      });
    }
  }, [place]);

  if (!place) return null;

  const toggleRestriction = (r) => {
    setForm((f) => ({
      ...f,
      restrictions: f.restrictions.includes(r) ? f.restrictions.filter((x) => x !== r) : [...f.restrictions, r],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      };
      const { data } = await api.put(`/moderation/places/${place.place_id}/edit`, payload);
      toast.success("Lugar atualizado");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-place-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Editar lugar</DialogTitle>
          <DialogDescription className="text-brand-textMuted">
            Atualize as informações do lugar. As mudanças entram em vigor imediatamente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Nome</label>
            <Input data-testid="edit-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Endereço</label>
            <Input data-testid="edit-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-bold text-brand-text mb-1">Cidade</label>
              <Input data-testid="edit-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">UF</label>
              <select data-testid="edit-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full bg-brand-bg border border-brand-border rounded-2xl h-11 px-3 font-bold">
                {BRAZIL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Restrições</label>
            <div className="flex flex-wrap gap-2">
              {RESTRICTIONS.map((r) => {
                const active = form.restrictions.includes(r.value);
                return (
                  <button key={r.value} type="button" onClick={() => toggleRestriction(r.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 transition-all ${active ? "border-transparent text-white" : "border-brand-border bg-white text-brand-text hover:border-brand-primary"}`}
                    style={active ? { background: r.text, borderColor: r.text } : {}}
                  >{r.emoji} {r.label}</button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Descrição</label>
            <Textarea data-testid="edit-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Site</label>
              <Input data-testid="edit-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Telefone</label>
              <Input data-testid="edit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Latitude</label>
              <Input data-testid="edit-lat" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Longitude</label>
              <Input data-testid="edit-lng" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Status</label>
            <select data-testid="edit-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-brand-bg border border-brand-border rounded-2xl h-11 px-3 font-bold">
              <option value="pending">Pendente</option>
              <option value="approved">Aprovado</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full font-bold">Cancelar</Button>
            <Button type="submit" data-testid="edit-save-btn" disabled={saving} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
